<ng-template #tooltip let-tooltip="tooltip" let-icon="icon">
  @if (tooltip) {
    <app-icon class="ms-2" [appTooltip]="tooltip | translate" [attr.aria-label]="tooltip | translate" [icon]="icon"></app-icon>
  }
</ng-template>
<div class="table-responsive" [ngStyle]="config?.responsiveContainerStyles">
  <table class="table table-hover" [class.selected]="rowSelection" [ngStyle]="config?.styles" [attr.role]="config?.tableRole">
    @if (isTableDataAvailable() && config.caption) {
      <caption class="visuallyhidden">
        {{
          config.caption
        }}
      </caption>
    }
    <thead>
      <tr>
        @if (config?.singleSelect?.selectionRadioButtons) {
          <th class="radio" scope="col">
            {{ 'select_row' | translate }}
          </th>
        }
        @for (column of columns; track column) {
          @if (config?.columns[column]?.sortingEnabled) {
            <th
              scope="col"
              [appAriaSort]="sortingOrder[column]"
              #sort="appAriaSort"
              [ngStyle]="config?.columns[column]?.styles"
              [class.sortable]="true"
            >
              @if (config?.columns[column]?.selectAllEnabled) {
                <input
                  type="checkbox"
                  [ngModel]="allSelected"
                  (change)="allRowsSelected($event)"
                  [attr.aria-label]="
                    config.columns[column]?.translationKey | translate: config?.columns[column]?.translationExtra
                  "
                />
              }
              <button
                [appAriaLabelSort]="sort.ariaSort"
                [sortColumn]="config.columns[column]?.translationKey"
                [sortColumnExtra]="config.columns[column]?.translationExtra"
                type="button"
                class="btn btn-link d-flex align-items-center p-0 fw-bold text-decoration-none text-black text-start"
                (click)="sortingTriggered(column)"
              >
                @if (!config?.columns[column]?.hideLabel) {
                  {{ config?.columns[column]?.translationKey | translate: config?.columns[column]?.translationExtra }}
                }
                @if (config?.columns[column]?.tooltip) {
                  <ds-icon
                    [icon]="config?.columns[column]?.tooltip.icon"
                    [color]="config?.columns[column]?.styles?.color"
                    [ds-tooltip]="config?.columns[column]?.tooltip.translationKey | translate"
                    [tooltipPlacement]="DsTooltipPosition.top"
                    class="ms-2"
                  ></ds-icon>
                }
                @if (config?.columns[column]?.headerIcon) {
                  @if (config?.columns[column]?.headerIcon.icon) {
                    <ds-icon
                      [icon]="config?.columns[column]?.headerIcon.icon"
                      [color]="config?.columns[column]?.headerIcon.color"
                      class="ms-2"
                    ></ds-icon>
                  }
                  @if (config?.columns[column]?.headerIcon.template) {
                    <ng-container [ngTemplateOutlet]="config?.columns[column]?.headerIcon.template"></ng-container>
                  }
                }
                @switch (sortingOrder[column]) {
                  @case ($sortingOrder.asc) {
                    <svg>
                      <use xlink:href="#fs-sort-up"></use>
                    </svg>
                  }
                  @case ($sortingOrder.desc) {
                    <svg>
                      <use xlink:href="#fs-sort-down"></use>
                    </svg>
                  }
                  @default {
                    <svg>
                      <use xlink:href="#fs-sort"></use>
                    </svg>
                  }
                }
              </button>
            </th>
          } @else {
            <th scope="col" [ngStyle]="config?.columns[column]?.styles" [class.sortable]="false">
              @if (config?.columns[column]?.selectAllEnabled) {
                <input
                  type="checkbox"
                  [ngModel]="allSelected"
                  (change)="allRowsSelected($event)"
                  [attr.aria-label]="
                    config.columns[column]?.translationKey | translate: config?.columns[column]?.translationExtra
                  "
                />
              }
              @if (!config?.columns[column]?.hideLabel) {
                {{ config?.columns[column]?.translationKey | translate: config?.columns[column]?.translationExtra }}
              }
              @if (config?.columns[column]?.tooltip) {
                <ds-icon
                  [icon]="config?.columns[column]?.tooltip.icon"
                  [color]="config?.columns[column]?.styles?.color"
                  [ds-tooltip]="config?.columns[column]?.tooltip.translationKey | translate"
                  [tooltipPlacement]="DsTooltipPosition.top"
                  class="ms-2"
                ></ds-icon>
              }
              @if (config?.columns[column]?.headerIcon) {
                @if (config?.columns[column]?.headerIcon.icon) {
                  <ds-icon
                    [icon]="config?.columns[column]?.headerIcon.icon"
                    [color]="config?.columns[column]?.headerIcon.color"
                    class="ms-2"
                  ></ds-icon>
                }
                @if (config?.columns[column]?.headerIcon.template) {
                  <ng-container [ngTemplateOutlet]="config?.columns[column]?.headerIcon.template"></ng-container>
                }
              }
            </th>
          }
        }
      </tr>
    </thead>
    <tbody>
      @if (isTableDataAvailable() && data.length > 0) {
        @for (row of data; track trackByFn(index, row); let index = $index) {
          <tr
            [ngStyle]="row.styles"
            (click)="rowSelected(row, index)"
            [class.selected]="isRowSelected(index)"
            [class.highlighted]="row.isHighlighted"
            [class.disabled]="row.isDisabled"
            [class.inactive]="row.isInactive"
            [attr.data-e2e-id]="row.key"
          >
            @if (config?.singleSelect?.selectionRadioButtons) {
              <td>
                <input
                  type="radio"
                  [value]="index"
                  [(ngModel)]="rowSelection.index"
                  (ngModelChange)="rowSelected(row, index)"
                  [disabled]="row.isDisabled"
                  [attr.aria-label]="getRadioButtonAriaLabel(index)"
                />
              </td>
            }
            @for (column of columns; track column) {
              @if (row.data[column]) {
                <td
                  [colSpan]="row.data[column]?.config?.colSpan"
                  [ngStyle]="getCellStyles(row, column)"
                  [attr.data-type]="row.data[column]?.type?.cellDataType"
                >
                  <dcl-wrapper [type]="row.data[column]?.type" [config]="row.data[column]?.config"></dcl-wrapper>
                </td>
              }
            }
          </tr>
        }
      } @else {
        @if (isDataLoading) {
          @if (config.emptyState) {
            <tr class="non-hoverable">
              <td [colSpan]="colSpan">
                <div class="row w-100">
                  <div class="col-12 d-flex flex-column justify-content-center align-items-center">
                    <h3 class="mt-4 mb-2">{{ 'loading_spinner_text' | translate }}</h3>
                  </div>
                </div>
              </td>
            </tr>
          }
        } @else {
          @if (config.emptyState) {
            <tr class="non-hoverable">
              <td [colSpan]="colSpan">
                <table-empty-state [config]="config.emptyState"></table-empty-state>
              </td>
            </tr>
          }
        }
      }
    </tbody>
  </table>
</div>
